import React, { ReactElement } from 'react';
import { Modal } from '@odin-labs/components';
import { getDateTimeMoment } from 'utils/dates';
import { cn } from 'utils';
import { STAT_PLACEHOLDER } from 'containers/jobsite/tabs/jobsiteOverview/helpers';
import type { WeatherCardConditions } from './JobsiteOverviewWeatherCard';

export type JobsiteOverviewWeatherDrawerProps = {
  isOpen: boolean;
  closeDrawer: () => void;
  weatherConditions: WeatherCardConditions[];
  timeZone: string | null;
};

const formatTemp = (value: number | null | undefined): string =>
  value == null ? STAT_PLACEHOLDER : `${Math.round(value)}° F`;

const formatNumeric = (
  value: number | null | undefined,
  unit: string,
): string =>
  value == null ? STAT_PLACEHOLDER : `${Math.round(value)} ${unit}`.trim();

const formatWind = (conditions: WeatherCardConditions): string => {
  const { windSpeed, windDirectionCardinal, windDirectionDegrees } = conditions;
  if (
    windSpeed == null &&
    windDirectionCardinal == null &&
    windDirectionDegrees == null
  ) {
    return STAT_PLACEHOLDER;
  }
  const speedPart =
    windSpeed == null ? STAT_PLACEHOLDER : `${Math.round(windSpeed)} mph`;
  const directionPart = [
    windDirectionCardinal,
    windDirectionDegrees != null
      ? `${Math.round(windDirectionDegrees)}°`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return directionPart ? `${speedPart} (${directionPart})` : speedPart;
};

type RowProps = { label: string; value: string };
function Row({ label, value }: RowProps): ReactElement {
  return (
    <div className="odin-flex odin-flex-row odin-justify-between odin-gap-2">
      <span className="odin-text-xs odin-text-odin-dark-gray">{label}</span>
      <span className="odin-text-xs odin-text-odin-navy odin-font-medium">
        {value}
      </span>
    </div>
  );
}

export function JobsiteOverviewWeatherDrawer({
  isOpen,
  closeDrawer,
  weatherConditions,
  timeZone,
}: JobsiteOverviewWeatherDrawerProps): ReactElement {
  const sortedConditions = React.useMemo(() => {
    return [...weatherConditions].sort((a, b) => {
      const aTs = a.timestamp ? new Date(a.timestamp).valueOf() : 0;
      const bTs = b.timestamp ? new Date(b.timestamp).valueOf() : 0;
      return aTs - bTs;
    });
  }, [weatherConditions]);

  return (
    <Modal
      open={isOpen}
      setOpen={closeDrawer}
      variant="drawer"
      size="xs"
      title="Hourly weather"
      actionsPanel={null}
    >
      {sortedConditions.length === 0 ? (
        <p className="odin-text-sm odin-text-odin-dark-gray">
          No weather data available for this day.
        </p>
      ) : (
        <div className="odin-flex odin-flex-col odin-gap-3">
          {sortedConditions.map((conditions) => {
            const localMoment = getDateTimeMoment({
              date: conditions.timestamp,
              timeZone: timeZone ?? undefined,
              isUTC: true,
            });
            const hourLabel = localMoment?.format('ha') ?? STAT_PLACEHOLDER;
            const key = conditions.timestamp ?? hourLabel;
            return (
              <div
                key={key}
                className={cn(
                  'odin-flex odin-flex-col odin-gap-2',
                  'odin-rounded-xl odin-border odin-border-odin-medium-gray',
                  'odin-p-3',
                )}
              >
                <div className="odin-flex odin-flex-row odin-items-center odin-gap-3">
                  <span className="odin-text-sm odin-font-semibold odin-text-odin-navy odin-w-12">
                    {hourLabel}
                  </span>
                  {conditions.icon ? (
                    <img
                      src={conditions.icon}
                      alt={conditions.description ?? 'weather'}
                      width={32}
                      height={32}
                    />
                  ) : (
                    <div className="odin-w-8 odin-h-8" />
                  )}
                  <span className="odin-text-base odin-font-semibold odin-text-odin-navy">
                    {formatTemp(conditions.temp)}
                  </span>
                  <span className="odin-text-xs odin-text-odin-dark-gray odin-truncate">
                    {conditions.description ?? STAT_PLACEHOLDER}
                  </span>
                </div>
                <div className="odin-flex odin-flex-col odin-gap-1">
                  <Row
                    label="Humidity"
                    value={formatNumeric(conditions.humidity, '%')}
                  />
                  <Row
                    label="Visibility"
                    value={formatNumeric(conditions.visibility, 'mi')}
                  />
                  <Row label="Wind" value={formatWind(conditions)} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
